/**
 * Three-dot typing indicator for the pause before the first streamed token
 * (adaptive thinking can be silent for a few seconds — without this it reads
 * as a hang). Styled as an assistant bubble so it sits naturally in the thread.
 * `role="status"` + the localized aria-label keep it legible to screen readers.
 *
 * An optional `label` names what is happening ("Checking your plan…" /
 * "Creating the task…") while the assistant runs a tool.
 */

import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface AssistantTypingIndicatorProps {
  label?: string;
}

const AssistantTypingIndicator = ({ label }: AssistantTypingIndicatorProps) => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      role="status"
      aria-label={label || t('assistant.status.thinking')}
      sx={{
        alignSelf: 'flex-start',
        bgcolor: 'background.elevation2',
        borderRadius: (theme) => theme.spacing(0, 2, 2, 2),
        px: 2,
        py: 1.75,
        '@keyframes assistantTypingDot': {
          '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
          '40%': { transform: 'scale(1)', opacity: 1 },
        },
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: 'text.secondary',
            animation: 'assistantTypingDot 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          {label}
        </Typography>
      )}
    </Stack>
  );
};

export default AssistantTypingIndicator;
